import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { OnboardingService, OnboardingChecklistItem } from '../../../core/services/onboarding.service';
import { CustomClaimService } from '../../../core/services/custom-claim.service';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil, filter, take } from 'rxjs/operators';

/**
 * @deprecated Replaced by UnifiedOnboardingComponent as of 2026-02-13.
 * See: agent-os/specs/2026-02-13-unified-onboarding-redesign/spec.md
 * This component is kept for reference but is no longer used in the application.
 * Safe to delete in a future cleanup task.
 */
@Component({
  selector: 'app-onboarding-checklist',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './onboarding-checklist.component.html',
  styleUrls: ['./onboarding-checklist.component.scss']
})
export class OnboardingChecklistComponent implements OnInit, OnDestroy {
  checklist$!: Observable<OnboardingChecklistItem[]>;
  completionPercentage$!: Observable<number>;
  completedCount$!: Observable<{ completed: number; total: number }>;
  onboardingCompleted$!: Observable<boolean>;
  onboardingDismissed$!: Observable<boolean>;
  welcomeMessageShown$!: Observable<boolean>;

  // Observable to check if user is Owner or Chief Supervisor
  isOwnerOrChiefSupervisor$!: Observable<boolean>;

  // Combined observable for showing welcome message (completed + not shown this session + is Owner/CS)
  showWelcomeMessage$!: Observable<boolean>;

  private destroy$ = new Subject<void>();

  constructor(
    private onboardingService: OnboardingService,
    private customClaimService: CustomClaimService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Filter checklist to only show visible items
    this.checklist$ = this.onboardingService.getChecklist().pipe(
      map(checklist => checklist.filter(item => item.isVisible))
    );
    this.onboardingCompleted$ = this.onboardingService.onboardingCompleted$;
    this.onboardingDismissed$ = this.onboardingService.onboardingDismissed$;
    this.welcomeMessageShown$ = this.onboardingService.welcomeMessageShown$;

    // Check if user is Owner or Chief Supervisor (only they see the owner onboarding)
    this.isOwnerOrChiefSupervisor$ = this.customClaimService.isOwnerOrChiefSupervisor$();

    this.completionPercentage$ = this.checklist$.pipe(
      map(checklist => this.onboardingService.getCompletionPercentage(checklist))
    );

    this.completedCount$ = this.checklist$.pipe(
      map(checklist => this.onboardingService.getCompletedCount(checklist))
    );

    // Combined observable: show welcome only if completed, not yet shown this session
    this.showWelcomeMessage$ = combineLatest([
      this.onboardingCompleted$,
      this.welcomeMessageShown$,
      this.isOwnerOrChiefSupervisor$
    ]).pipe(
      map(([completed, shown, isOwnerOrCS]) => completed && !shown && isOwnerOrCS)
    );

    // Auto-mark welcome message as shown after it displays (once per session)
    this.showWelcomeMessage$.pipe(
      filter(show => show),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      // Mark as shown after a brief delay to ensure the message is visible
      setTimeout(() => {
        this.onboardingService.markWelcomeMessageShown();
      }, 5000); // Show for 5 seconds before marking as shown
    });

    // Sync checklist with actual data state
    this.onboardingService.syncWithActualState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Navigate to action route for item
   */
  navigateToItem(item: OnboardingChecklistItem): void {
    if (item.actionRoute && !item.isCompleted) {
      this.router.navigate([item.actionRoute]);
    }
  }

  /**
   * Dismiss the checklist
   */
  dismissChecklist(): void {
    this.onboardingService.dismissOnboarding().subscribe();
  }

  /**
   * Mark onboarding as complete
   */
  completeOnboarding(): void {
    this.onboardingService.completeOnboarding().subscribe();
  }
}
